import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  ButtonGroup,
  CircularProgress,
  Alert,
  Paper,
  Divider,
  Container,
  Grid,
  Stack
} from '@mui/material';
import {
  Storefront as StoreIcon,
  Person as PersonIcon,
  CalendarToday as CalendarIcon,
  PlayArrow as PlayIcon,
  Terminal as TerminalIcon,
  CheckCircleOutline as SuccessIcon
} from '@mui/icons-material';

export default function DummyDataGenerator() {
  const [formData, setFormData] = useState({
    storeID: 'SZG104000',
    clientId: 'UUD608263',
    targetDate: new Date().toISOString().split('T')[0]
  });

  const [loading, setLoading] = useState(false);
  const [logs, setLogs] = useState([]);
  const [status, setStatus] = useState({ type: null, message: '' });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const addLog = (msg) => {
    setLogs((prev) => [...prev, `[${new Date().toLocaleTimeString()}] ${msg}`]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setLogs([]);
    setStatus({ type: null, message: '' });

    addLog(`Initiating generator job for Store: ${formData.storeID}...`);
    addLog(`Target Business Date: ${formData.targetDate}`);

    try {
      // POST call to your internal server endpoint
      const response = await fetch('http://localhost:1717/v1/device/generate-dummy-day', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          // 'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(formData)
      });

      const result = await response.json();

      if (response.ok && result.code === 0) {
        addLog('✅ Start Day initialized successfully.');
        addLog('✅ Generated orders, cancellations, paid outs & drops.');
        addLog(`🏁 Day closed successfully. Total Orders: ${result.data?.c_order || 0}`);

        setStatus({
          type: 'success',
          message: `Dummy data generated successfully for ${formData.targetDate}!`
        });
      } else {
        throw new Error(result.msg || 'Failed to execute dummy data generation.');
      }
    } catch (err) {
      addLog(`❌ ERROR: ${err.message}`);
      setStatus({
        type: 'error',
        message: err.message
      });
    } finally {
      setLoading(false);
    }
  };

  const setPresetDate = (daysAgo) => {
    const d = new Date();
    d.setDate(d.getDate() - daysAgo);
    setFormData((prev) => ({
      ...prev,
      targetDate: d.toISOString().split('T')[0]
    }));
  };

  return (
    <Container maxWidth="lg" sx={{ py: 6 }}>
      <Grid container spacing={3}>
        
        {/* Left Side: Input Form Card */}
        <Grid item xs={12} md={7}>
          <Card elevation={4} sx={{ borderRadius: 3 }}>
            <CardContent sx={{ p: 4 }}>
              
              {/* Card Header */}
              <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 3 }}>
                <Box
                  sx={{
                    p: 1.5,
                    bgcolor: 'primary.light',
                    color: 'primary.main',
                    borderRadius: 2,
                    display: 'flex'
                  }}
                >
                  <StoreIcon fontSize="medium" />
                </Box>
                <Box>
                  <Typography variant="h6" fontWeight="bold">
                    Daily Data Simulator
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Generate random orders, cancellations, and cash events
                  </Typography>
                </Box>
              </Stack>

              <Divider sx={{ mb: 3 }} />

              <Box component="form" onSubmit={handleSubmit} noValidate>
                <Stack spacing={3}>
                  
                  {/* Store ID Field */}
                  <TextField
                    fullWidth
                    label="Store ID"
                    name="storeID"
                    value={formData.storeID}
                    onChange={handleChange}
                    required
                    InputProps={{
                      startAdornment: (
                        <StoreIcon color="action" sx={{ mr: 1 }} />
                      )
                    }}
                  />

                  {/* Client / Staff ID Field */}
                  <TextField
                    fullWidth
                    label="Client / Staff ID"
                    name="clientID"
                    value={formData.clientID}
                    onChange={handleChange}
                    required
                    InputProps={{
                      startAdornment: (
                        <PersonIcon color="action" sx={{ mr: 1 }} />
                      )
                    }}
                  />

                  {/* Target Date Field & Quick Presets */}
                  <Box>
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                      sx={{ mb: 1 }}
                    >
                      <Typography variant="caption" color="text.secondary" fontWeight="bold">
                        TARGET BUSINESS DATE
                      </Typography>
                      <ButtonGroup size="small" variant="outlined">
                        <Button onClick={() => setPresetDate(0)}>Today</Button>
                        <Button onClick={() => setPresetDate(1)}>Yesterday</Button>
                      </ButtonGroup>
                    </Stack>
                    <TextField
                      fullWidth
                      type="date"
                      name="targetDate"
                      value={formData.targetDate}
                      onChange={handleChange}
                      required
                      InputProps={{
                        startAdornment: (
                          <CalendarIcon color="action" sx={{ mr: 1 }} />
                        )
                      }}
                    />
                  </Box>

                  {/* Feedback Status Alert */}
                  {status.message && (
                    <Alert severity={status.type} icon={status.type === 'success' ? <SuccessIcon /> : undefined}>
                      {status.message}
                    </Alert>
                  )}

                  {/* Submit Button */}
                  <Button
                    type="submit"
                    variant="contained"
                    size="large"
                    disabled={loading}
                    startIcon={
                      loading ? (
                        <CircularProgress size={20} color="inherit" />
                      ) : (
                        <PlayIcon />
                      )
                    }
                    sx={{ py: 1.5, borderRadius: 2, fontWeight: 'bold' }}
                  >
                    {loading ? 'Generating Business Day...' : 'Execute Full Business Day'}
                  </Button>

                </Stack>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Right Side: Live Terminal Log View */}
        <Grid item xs={12} md={5}>
          <Paper
            elevation={4}
            sx={{
              p: 3,
              borderRadius: 3,
              bgcolor: 'grey.900',
              color: 'grey.100',
              height: '100%',
              minHeight: 400,
              display: 'flex',
              flexDirection: 'column'
            }}
          >
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
              <TerminalIcon color="primary" />
              <Typography variant="subtitle2" fontWeight="bold" sx={{ color: 'grey.300' }}>
                Live Execution Logs
              </Typography>
            </Stack>

            <Divider sx={{ borderColor: 'grey.800', mb: 2 }} />

            <Box
              sx={{
                flexGrow: 1,
                fontFamily: 'monospace',
                fontSize: '0.8rem',
                overflowY: 'auto',
                bgcolor: 'grey.950',
                p: 2,
                borderRadius: 2,
                maxHeight: 350
              }}
            >
              {logs.length === 0 ? (
                <Typography variant="caption" sx={{ color: 'grey.600', fontStyle: 'italic' }}>
                  Console output will stream here after execution begins...
                </Typography>
              ) : (
                logs.map((log, index) => (
                  <Typography
                    key={index}
                    variant="body2"
                    sx={{ fontFamily: 'monospace', fontSize: '0.75rem', mb: 0.5, color: 'grey.300' }}
                  >
                    {log}
                  </Typography>
                ))
              )}
            </Box>
          </Paper>
        </Grid>

      </Grid>
    </Container>
  );
}